import React, { useState } from 'react';
import { motion } from 'framer-motion';

export interface AppIconProps {
  id: string;
  name: string;
  description?: string;
  icon: React.ReactNode;
  gradient?: string;
  isActive?: boolean;
  isFolder?: boolean;
  itemCount?: number;
  onClick: () => void;
}

export const AppIcon: React.FC<AppIconProps> = ({
  id,
  name,
  description,
  icon,
  gradient = 'from-emerald-500 to-teal-600',
  isActive = false,
  isFolder = false,
  itemCount,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const renderedIcon = React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<{ isHovered?: boolean }>, {
        isHovered,
      })
    : icon;

  return (
    <motion.button
      type="button"
      layoutId={`app-tile-${id}`}
      whileHover={{ scale: 1.06, y: -2 }}
      whileTap={{ scale: 0.94 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30, mass: 1 }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group flex flex-col items-center text-center p-3 rounded-2xl transition-colors hover:bg-slate-100/70 focus:outline-none relative"
    >
      {/* Icon Card Container */}
      <motion.div
        layoutId={`app-icon-bg-${id}`}
        className={`relative w-15 h-15 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br ${gradient} text-white transition-all duration-300 group-hover:shadow-lg group-hover:shadow-emerald-500/20`}
        style={{
          boxShadow: isActive
            ? '0 0 0 3px rgba(16, 185, 129, 0.4), 0 8px 16px -4px rgba(0, 0, 0, 0.15)'
            : '0 4px 12px -2px rgba(0, 0, 0, 0.12)',
        }}
      >
        {/* Subtle glass reflection overlay */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-white/30 via-transparent to-black/5 pointer-events-none" />

        {/* Icon */}
        <div className="relative z-10 flex items-center justify-center [&>div]:flex [&>div]:items-center [&>div]:justify-center [&>img]:w-7 [&>img]:h-7 [&>svg]:w-7 [&>svg]:h-7 [&>div>svg]:w-7 [&>div>svg]:h-7">
          {renderedIcon}
        </div>

        {/* Active Dot Badge */}
        {isActive && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full shadow-sm" />
        )}

        {/* Folder badge with count */}
        {isFolder && itemCount && itemCount > 0 && (
          <span className="absolute -bottom-1 -right-1 text-[10px] font-bold px-1.5 py-0.5 bg-white text-emerald-700 border border-emerald-200 shadow-sm rounded-full">
            {itemCount}
          </span>
        )}
      </motion.div>

      {/* Label */}
      <span
        className={`mt-2 text-[12px] font-semibold tracking-tight transition-colors line-clamp-1 max-w-[90px] ${
          isActive ? 'text-emerald-700 font-bold' : 'text-slate-700 group-hover:text-slate-900'
        }`}
      >
        {name}
      </span>

      {description && (
        <span className="text-[10px] text-slate-400 group-hover:text-slate-500 line-clamp-1 max-w-[100px] mt-0.5 transition-colors">
          {description}
        </span>
      )}
    </motion.button>
  );
};
